import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Sparkles, Tags } from "lucide-react";
import { toast } from "sonner";
import { CategoryPicker } from "@/components/common/CategoryPicker";
import { EXPENSE_CATS } from "@/components/finance/ExpensesCard";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";

const CATS = { ...EXPENSE_CATS, banque: "Opérations bancaires" };
const SHOWN = 4;
const UNDO_MS = 4000;

// Delays the API call so the user can cancel; flushes it if the card unmounts.
const useUndoable = (onPending) => {
  const [pending, setPending] = useState(null);
  const timer = useRef(null);
  const job = useRef(null);
  useEffect(() => () => {
    if (timer.current) { clearTimeout(timer.current); job.current?.(); }
  }, []);
  const schedule = (p, fn) => {
    setPending(p);
    onPending(true);
    job.current = async () => { try { await fn(); } catch (e) { setPending(null); toast.error(formatApiError(e)); } finally { onPending(false); } };
    timer.current = setTimeout(() => { timer.current = null; job.current(); }, UNDO_MS);
  };
  const undo = () => { clearTimeout(timer.current); timer.current = null; job.current = null; setPending(null); onPending(false); };
  return { pending, schedule, undo };
};

const PendingRow = ({ t, label, undo }) => (
  <li className="flex items-center gap-2 rounded-lg bg-success/10 px-2 py-2 text-sm animate-in fade-in" data-testid={`review-tx-${t.bridge_id}`}>
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success text-white"><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>
    <div className="min-w-0 flex-1">
      <div className="truncate text-[13px] font-medium leading-tight">{t.label}</div>
      <div className="text-[11px] font-semibold text-success" data-testid={`review-validated-${t.bridge_id}`}>Validée · {label}</div>
    </div>
    <button onClick={undo} className="shrink-0 rounded-full border border-border bg-card px-3 py-1 text-[11px] font-semibold hover:bg-secondary" data-testid={`review-undo-${t.bridge_id}`}>Annuler</button>
  </li>
);

const Row = ({ t, refresh, onPending }) => {
  const { money } = useFormat();
  const [open, setOpen] = useState(false);
  const { pending, schedule, undo } = useUndoable((on) => onPending(t.bridge_id, on));
  const set = (category) => schedule({ category }, async () => {
    const { data } = await api.patch(`/finance/transactions/${t.bridge_id}/category`, { category });
    if (data.also_updated) toast.success(`${data.also_updated} opération(s) similaire(s) mise(s) à jour`);
    refresh();
  });
  const confirm = () => schedule({ category: t.category }, async () => { await api.post(`/finance/transactions/${t.bridge_id}/confirm`); refresh(); });
  if (pending) return <PendingRow t={t} label={CATS[pending.category]} undo={undo} />;
  return (
    <li className="flex items-center gap-2 py-2 text-sm" data-testid={`review-tx-${t.bridge_id}`}>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-medium leading-tight">{t.label}</div>
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
          {new Date(t.date).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} · <span className="num">{money(-t.amount, { maximumFractionDigits: 2 })}</span>
          {t.status === "auto" && <Sparkles className="h-3 w-3 text-primary" aria-label="catégorisée automatiquement" />}
        </div>
      </div>
      <CategoryPicker cats={CATS} frequentUrl="/finance/categories/frequent" value={t.category} onPick={set} open={open} onOpenChange={setOpen} op={{ date: t.date, label: t.label, amount: -Math.abs(t.amount) }} placeholder="Catégorie"
        triggerClass="h-7 w-auto max-w-[8.5rem] shrink-0 px-2.5 text-[11px]" testid={`review-category-${t.bridge_id}`} />
      <button onClick={() => (t.status === "auto" ? confirm() : setOpen(true))} aria-label={t.status === "auto" ? "Valider l'opération" : "Choisir une catégorie"} title={t.status === "auto" ? "Valider l'opération" : "Choisissez la catégorie pour valider"}
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-transform active:scale-90 ${t.status === "auto" ? "bg-success text-white shadow-sm" : "border border-dashed border-muted-foreground/50 text-muted-foreground"}`} data-testid={`review-confirm-${t.bridge_id}`}>{t.status === "auto" ? <Check className="h-4 w-4" strokeWidth={3} /> : <Tags className="h-3.5 w-3.5" />}</button>
    </li>
  );
};

export const CategorizeCard = () => {
  const qc = useQueryClient();
  const [all, setAll] = useState(false);
  const [pend, setPend] = useState([]);
  const onPending = (id, on) => setPend((p) => (on ? [...new Set([...p, id])] : p.filter((x) => x !== id)));
  const { data } = useQuery({ queryKey: ["tx-review"], queryFn: () => api.get("/finance/transactions/review").then((r) => r.data) });
  const refresh = () => ["tx-review", "expense-summary", "budgets", "dashboard", "bank-txs", "health", "cat-frequent"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  if (!data?.items?.length) return null;
  const items = [...data.items].sort((a, b) => (a.status === "auto" ? 0 : 1) - (b.status === "auto" ? 0 : 1));
  const autos = items.filter((t) => t.status === "auto");
  const limit = SHOWN + pend.filter((id) => items.some((t) => t.bridge_id === id)).length;
  const confirmAll = async () => {
    try {
      await Promise.all(autos.map((t) => api.post(`/finance/transactions/${t.bridge_id}/confirm`)));
      toast.success(`${autos.length} catégorie(s) validée(s)`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className="card-surface tone-wash tone-sky p-4 sm:p-5" data-testid="categorize-card">
      <div className="flex items-center gap-2.5">
        <span className="tone-chip flex h-8 w-8 shrink-0 items-center justify-center rounded-xl"><Tags className="h-4 w-4" /></span>
        <h2 className="min-w-0 flex-1 truncate text-[15px] font-bold leading-tight sm:text-base">À vérifier
          {data.todo > 0 && <span className="num ml-1.5 rounded-full bg-primary px-1.5 py-0.5 align-middle text-[10px] font-bold text-primary-foreground" data-testid="categorize-todo">{data.todo}</span>}</h2>
        {autos.length > 1 && <button onClick={confirmAll} className="shrink-0 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold hover:bg-secondary" data-testid="categorize-confirm-all">Tout valider ({autos.length})</button>}
      </div>
      <p className="mt-1 hidden text-xs text-muted-foreground sm:block">Validez ou corrigez : CASHMY apprend de vos choix et catégorisera ensuite automatiquement.</p>
      <ul className="mt-1 divide-y divide-border" data-testid="categorize-list">{(all ? items : items.slice(0, limit)).map((t) => <Row key={t.bridge_id} t={t} refresh={refresh} onPending={onPending} />)}</ul>
      {items.length > limit && <button onClick={() => setAll(!all)} className="mt-1 text-xs font-semibold text-primary" data-testid="categorize-toggle">{all ? "Réduire" : `Voir les ${items.length - limit} autres`}</button>}
    </section>
  );
};
